import React, { useState, useRef } from "react";
import Select from 'react-select';
import { withTranslation, WithTranslation } from "react-i18next";
import { arrayMoveImmutable } from "array-move";
import { IOwnPropertyOrId } from "src/backend/interfaces/configuration/IConfiguration";
import styled from "styled-components";
import "spectre.css/dist/spectre.min.css";
import { useDrop, useDrag } from "react-dnd";

const MAX_COLUMNS = 10;

const TableHeader = styled.div`
  text-transform: uppercase;
  font-weight: bold;
`;

const Column = styled.div`
  width: 100%;
  border: solid 1px black;
  text-align: left;
  padding: 10px;
  margin: 10px;
  background-color: white;
  z-index 1000;
`;

const ColumnList = styled.div`
  display: block;
  margin: auto;
  position: relative;
  width: 500px;
`;

const RemoveButton = styled.button`
`;

export interface ITableColumn {
  value: IOwnPropertyOrId;
  label: string;
}

interface ISortableItemProps {
  type: string;
  id: IOwnPropertyOrId;
  text: string;
  index: number;
  onMove: (dragIndex: number, hoverIndex: number) => void;
  onRemove: (id: IOwnPropertyOrId) => void;
}

interface DragObjectWithIndex {
  type: string;
  index: number;
}

const SortableItem = ({type, id, text, index, onMove, onRemove}: ISortableItemProps) => {
  const ref = useRef(null);
  const [{ handlerId }, drop] = useDrop<DragObjectWithIndex, unknown, { handlerId: any;}>({
    accept: type,
    collect(monitor) {
      return {
        handlerId: monitor.getHandlerId()
      }
    },
    hover(item, monitor) {
      if (!ref.current) {
        return;
      }
      const dragIndex = item.index;
      const hoverIndex = index;
      // Don't replace items with themselves
      if (dragIndex === hoverIndex) {
        return;
      }
      // Determine rectangle on screen
      const hoverBoundingRect = ref.current?.getBoundingClientRect();
      // Get vertical middle
      const hoverMiddleY =
        (hoverBoundingRect.bottom - hoverBoundingRect.top) / 2;
      // Determine mouse position
      const clientOffset = monitor.getClientOffset();
      // Get pixels to the top
      const hoverClientY = clientOffset.y - hoverBoundingRect.top;
      // Only perform the move when the mouse has crossed half of the items height
      // When dragging downwards, only move when the cursor is below 50%
      // When dragging upwards, only move when the cursor is above 50%
      // Dragging downwards
      if (dragIndex < hoverIndex && hoverClientY < hoverMiddleY) {
        return;
      }
      // Dragging upwards
      if (dragIndex > hoverIndex && hoverClientY > hoverMiddleY) {
        return;
      }
      // Time to actually perform the action
      onMove(dragIndex, hoverIndex);
      // Note: we're mutating the monitor item here!
      // Generally it's better to avoid mutations,
      // but it's good here for the sake of performance
      // to avoid expensive index searches.
      item.index = hoverIndex;
    }
  });
  const [{ isDragging }, drag] = useDrag<DragObjectWithIndex, unknown, {isDragging: boolean}>({
    type: type,
    item: {type, index},
    collect: (monitor) => ({
      isDragging: monitor.isDragging(),
    }),
  })
  const opacity = isDragging ? 0 : 1
  drag(drop(ref));
  return (
    <Column style={{opacity: opacity}} ref={ref} data-handler-id={handlerId}>
      <RemoveButton onClick={() => onRemove(id)} className="btn btn-clear float-right"></RemoveButton>
      {text}
    </Column>
  );
}

interface IProps extends WithTranslation {
  objectTypeId: number;
  name: string;
  selectedColumns: IOwnPropertyOrId[];
  availableColumns: ITableColumn[];
  updateColumns: (objectType: number, columns: IOwnPropertyOrId[]) => void;
}

const TableColunmEditorBase = (props: IProps) => {

  const initColumns = (): ITableColumn[] => {
    const ret: ITableColumn[] = [];
    for(const column of props.selectedColumns) {
      ret.push({
        value: column,
        label: props.availableColumns.find(c => c.value === column)?.label ?? column.toString()
      });
    }
    return ret;
  }

  const initAvailableColumns = (selectedColumns: ITableColumn[]): ITableColumn[] => {
    const ret: ITableColumn[] = [];
    for(const column of props.availableColumns) {
      if(!selectedColumns.find(c => c.value === column.value)) {
        ret.push(column);
      }
    }
    return ret;
  }

  const [columns, setColumns] = useState(initColumns());
  const [availableColumns, setAvaliableColumns] = useState(initAvailableColumns(columns));

  const onMove = (dragIndex: number, hoverIndex: number) => {
    const newColumns = arrayMoveImmutable(columns, dragIndex, hoverIndex);
    setColumns(newColumns);
    props.updateColumns(props.objectTypeId, newColumns.map(i => i.value));
  }

  const onRemove = (id: IOwnPropertyOrId) => {
    const newColumns: ITableColumn[] = [];
    for(const column of columns) {
      if(column.value !== id) {
        newColumns.push(column);
      }
    }
    setColumns(newColumns);
    setAvaliableColumns(initAvailableColumns(newColumns));
    props.updateColumns(props.objectTypeId, newColumns.map(i => i.value));
  };

  const onAdd = (column: ITableColumn) => {
    const newColumns = columns;
    newColumns.push(column);
    setColumns(newColumns);
    setAvaliableColumns(initAvailableColumns(newColumns));
    props.updateColumns(props.objectTypeId, newColumns.map(i => i.value));
  }

  const renderAddNew = () => {

    const customStyles = {
      control: (provided) => ({
        ...provided,
        border: 'solid 1px black',
        borderRadius: 0,
        paddingTop: 3,
        paddingBottom: 3,
      }),
    };

    return (
      <div style={{marginLeft: 10, marginRight: -10, marginBottom: 10}}>
        <Select 
          styles={customStyles} 
          value={null}
          options={availableColumns} 
          onChange={(e) => onAdd(e)} 
        />
      </div>
    );
  }

  return (
    <div>
      <TableHeader>{props.name}</TableHeader>
      <ColumnList>
        {columns.map((value, index) => (
          <SortableItem key={value.value} id={value.value} index={index} text={value.label} onRemove={onRemove} onMove={onMove} type={"ot" + props.objectTypeId} />
        ))}
      </ColumnList>
      { columns.length < MAX_COLUMNS && renderAddNew() }
    </div>
  );
};

export const TableColumnEditor = withTranslation()(TableColunmEditorBase);
